'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  CalendarCheck,
  Info,
  Clock,
} from 'lucide-react';
import { useToast } from '@/components/ui/Toast';

interface LeaveFormValues {
  employeeId: string;
  department: string;
  typeOfLeave: string;
  startDate: string;
  endDate: string;
  reason: string;
}

const departments = ['Sales', 'Marketing', 'Operations', 'Finance', 'Packaging'];
const leaveTypes = ['Sick Leave', 'Casual Leave', 'Public Holiday'];

export default function ApprovalRequestFormContent() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<LeaveFormValues>();

  const watchStart = watch('startDate');
  const watchEnd = watch('endDate');

  const calcDays = () => {
    if (!watchStart || !watchEnd) return null;
    const s = new Date(watchStart);
    const e = new Date(watchEnd);
    const diff = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : null;
  };

  const days = calcDays();

  const onSubmit = async (_data: LeaveFormValues) => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/leave-requests
    await new Promise((r) => setTimeout(r, 1200));
    setIsSubmitting(false);
    setIsSuccess(true);
    toast('success', 'Leave Request Added Successfully', 'Your request has been submitted for approval.');
    setTimeout(() => setIsSuccess(false), 3500);
    reset();
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          href="/"
          className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-md hover:bg-muted"
        >
          <ArrowLeft size={18} />
        </Link>
        <div>
          <h1 className="text-2xl font-700 text-foreground">Leave Approval Request</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Submit a leave request for manager review and approval
          </p>
        </div>
      </div>

      {/* Workflow notice */}
      <div className="flex items-start gap-2.5 bg-secondary border border-primary/20 rounded-lg px-4 py-3 mb-6">
        <Info size={15} className="text-primary mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-600 text-foreground">Approval Workflow Active</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Your request will be routed to your department manager for review. You will be notified once a decision is made.
          </p>
        </div>
      </div>

      {isSuccess && (
        <div className="flex items-center gap-2.5 bg-success-bg border border-success/20 rounded-lg px-4 py-3 mb-6 fade-in">
          <CheckCircle2 size={16} className="text-success shrink-0" />
          <p className="text-sm font-600 text-success">Leave Request Added Successfully!</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="bg-card border border-border rounded-xl shadow-card divide-y divide-border">

          {/* Section: Employee Information */}
          <div className="px-5 py-4">
            <h2 className="text-sm font-700 text-foreground mb-4">Employee Information</h2>
            <div className="space-y-4">
              {/* Employee ID */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="employeeId">
                  Employee ID <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">
                  Enter your unique employee identifier (e.g. EMP-0042)
                </p>
                <input
                  id="employeeId"
                  type="text"
                  placeholder="e.g. EMP-0042"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow tabular-nums ${
                    errors.employeeId ? 'border-danger focus:ring-danger/30' : 'border-input'
                  }`}
                  {...register('employeeId', {
                    required: 'Employee ID is required',
                    pattern: {
                      value: /^EMP-\d{4}$/,
                      message: 'Format must be EMP-XXXX (e.g. EMP-0042)',
                    },
                  })}
                />
                {errors.employeeId && (
                  <p className="text-xs text-danger mt-1">{errors.employeeId.message}</p>
                )}
              </div>

              {/* Department */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="department">
                  Department <span className="text-danger">*</span>
                </label>
                <select
                  id="department"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                    errors.department ? 'border-danger focus:ring-danger/30' : 'border-input'
                  }`}
                  {...register('department', { required: 'Department is required' })}
                >
                  <option value="">Select department…</option>
                  {departments.map((d) => (
                    <option key={`dept-${d}`} value={d}>{d}</option>
                  ))}
                </select>
                {errors.department && (
                  <p className="text-xs text-danger mt-1">{errors.department.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Section: Leave Details */}
          <div className="px-5 py-4">
            <h2 className="text-sm font-700 text-foreground mb-4">Leave Details</h2>
            <div className="space-y-4">
              {/* Type of Leave */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="typeOfLeave">
                  Type of Leave <span className="text-danger">*</span>
                </label>
                <select
                  id="typeOfLeave"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                    errors.typeOfLeave ? 'border-danger focus:ring-danger/30' : 'border-input'
                  }`}
                  {...register('typeOfLeave', { required: 'Leave type is required' })}
                >
                  <option value="">Select leave type…</option>
                  {leaveTypes.map((lt) => (
                    <option key={`leave-${lt}`} value={lt}>{lt}</option>
                  ))}
                </select>
                {errors.typeOfLeave && (
                  <p className="text-xs text-danger mt-1">{errors.typeOfLeave.message}</p>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1" htmlFor="startDate">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">Weekdays only</p>
                  <input
                    id="startDate"
                    type="date"
                    className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                      errors.startDate ? 'border-danger focus:ring-danger/30' : 'border-input'
                    }`}
                    {...register('startDate', {
                      required: 'Start date is required',
                    })}
                  />
                  {errors.startDate && (
                    <p className="text-xs text-danger mt-1">{errors.startDate.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1" htmlFor="endDate">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">Weekdays only</p>
                  <input
                    id="endDate"
                    type="date"
                    className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                      errors.endDate ? 'border-danger focus:ring-danger/30' : 'border-input'
                    }`}
                    {...register('endDate', {
                      required: 'End date is required',
                      validate: (val) => {
                        if (!watchStart) return true;
                        return new Date(val) >= new Date(watchStart) || 'End date must be on or after start date';
                      },
                    })}
                  />
                  {errors.endDate && (
                    <p className="text-xs text-danger mt-1">{errors.endDate.message}</p>
                  )}
                </div>
              </div>

              {/* Duration indicator */}
              {days !== null && (
                <div className="flex items-center gap-2 bg-muted rounded-md px-3 py-2 fade-in">
                  <Clock size={13} className="text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    Duration:{' '}
                    <span className="font-700 text-foreground tabular-nums">
                      {days} day{days > 1 ? 's' : ''}
                    </span>
                  </p>
                </div>
              )}

              {/* Reason */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="reason">
                  Reason
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">
                  Optional — provide context to help your manager process the request faster
                </p>
                <textarea
                  id="reason"
                  rows={3}
                  placeholder="e.g. Scheduled medical procedure follow-up"
                  className="w-full rounded-md border border-input px-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow resize-none"
                  {...register('reason')}
                />
              </div>
            </div>
          </div>

          {/* Required fields note */}
          <div className="px-5 py-3 bg-muted/30">
            <p className="text-xs text-muted-foreground">
              Fields marked <span className="text-danger font-600">*</span> are required
            </p>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 mt-4 bg-background/95 backdrop-blur border-t border-border py-3 flex items-center justify-between gap-3 -mx-6 px-6 lg:-mx-8 lg:px-8 xl:-mx-10 xl:px-10 2xl:-mx-12 2xl:px-12">
          <button
            type="button"
            onClick={() => reset()}
            className="px-4 py-2 rounded-md border border-border text-sm font-600 text-foreground hover:bg-muted transition-all duration-150 active:scale-95"
          >
            Reset
          </button>
          <div className="flex items-center gap-2">
            <Link
              href="/"
              className="px-4 py-2 rounded-md border border-border text-sm font-600 text-foreground hover:bg-muted transition-all duration-150 active:scale-95"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed min-w-[130px] justify-center"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <CalendarCheck size={14} />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}